/**
 * extractedTextQuality — shared predicates over extracted `textContent`.
 *
 * Doc-extraction DIM-1 (Document-Image Misclassification, Redhead
 * "Kinder Morgen ROM" uploads 2026-08-03). One module owns the answers
 * to two questions every extraction consumer keeps re-deriving (and
 * re-deriving differently — that divergence WAS the bug):
 *
 *   1. Is this `textContent` real extracted content, or one of the
 *      bracketed `[…]` placeholders the extractors emit when text
 *      isn't available? (`isPlaceholderExtractedText` /
 *      `hasUsableExtractedText`)
 *   2. Is this image upload's vision extraction an image OF A DOCUMENT
 *      (screenshot / scan / photo of a spreadsheet, letter, table…)
 *      rather than a photograph of a scene?
 *      (`looksLikeDocumentImageText`)
 *
 * Why a dedicated module: `docClassifier.hasUsableText` and
 * `evidenceReadiness.computeDocQuality` carried hand-mirrored guards
 * that (a) treated the `[Image vision: …]` envelope — REAL vision
 * content — as unusable because it shares the `[Image` prefix with the
 * failure placeholders, and (b) treated `[PDF: … — no extractable
 * text]` placeholders as usable because `[PDF:` was on neither list.
 * Both call sites now delegate here. No imports — this module sits
 * below `docClassifier`, `driveConnector`, `documentUpload`, and
 * `lib/evidenceReadiness` in the dependency graph.
 *
 * Placeholder invariant (all emitters live in `documentUpload.ts`):
 * every placeholder is a SINGLE bracketed line — `[PDF: …]`,
 * `[Image: …]`, `[Binary file: …]`, `[Geographic data: …]` etc. —
 * while the one bracket-prefixed REAL extraction, the image-vision
 * envelope, is `[Image vision: <name>]\n\n<body>` with a substantive
 * body. "Starts with `[` and has no substantive body after line 1"
 * therefore classifies every current and future placeholder without
 * enumerating wordings.
 */

/** Envelope prefix `extractImageDescription` stamps on a SUCCESSFUL
 *  vision extraction. Distinct from the `[Image: …]` failure
 *  placeholders (too large / vision returned nothing / vision threw). */
export const IMAGE_VISION_TEXT_PREFIX = '[Image vision:';

/** Minimum substantive body (chars, post-trim) for bracket-prefixed
 *  text to count as content rather than placeholder. Mirrors the
 *  40-char floor `hasUsableText` has always applied to plain text. */
const MIN_USABLE_BODY_CHARS = 40;

/**
 * True when `text` is one of the extraction placeholders (or empty).
 * A placeholder is a bracket-opened first line with no substantive
 * body after it. The `[Image vision: …]` envelope carries its real
 * content on the following lines, so it fails this test — which is
 * exactly right.
 */
export function isPlaceholderExtractedText(text: string | undefined | null): boolean {
  const t = (text ?? '').trim();
  if (!t) return true;
  if (!t.startsWith('[')) return false;
  const firstLineEnd = t.indexOf('\n');
  const body = firstLineEnd === -1 ? '' : t.slice(firstLineEnd + 1).trim();
  return body.length < MIN_USABLE_BODY_CHARS;
}

/**
 * True when `text` is real extracted content a classifier / summariser
 * / evidence scorer may read. Successor to `docClassifier.hasUsableText`
 * (which delegates here) with two corrections:
 *   - `[Image vision: …]` envelopes WITH a body are usable (they carry
 *     the vision extraction — 2,789 accurate chars in the live
 *     Kinder Morgan case);
 *   - `[PDF: … — no extractable text]` placeholders are NOT usable
 *     (previously slipped through because only `[Binary file` /
 *     `[Image` / `[Geographic` / `[Video` / `[Extraction failed`
 *     prefixes were rejected).
 */
export function hasUsableExtractedText(text: string | undefined | null): boolean {
  const t = (text ?? '').trim();
  if (t.length < MIN_USABLE_BODY_CHARS) return false;
  return !isPlaceholderExtractedText(t);
}

/** Strip the `[Image vision: <name>]` envelope line, returning the
 *  vision body. Returns null when `text` is not a vision envelope. */
export function visionBodyOf(text: string | undefined | null): string | null {
  const t = (text ?? '').trim();
  if (!t.startsWith(IMAGE_VISION_TEXT_PREFIX)) return null;
  const firstLineEnd = t.indexOf('\n');
  if (firstLineEnd === -1) return '';
  return t.slice(firstLineEnd + 1).trim();
}

/**
 * Machine-readable content-type marker `describeImageForSite` asks the
 * vision model to open with (DIM-1). First body line is one of:
 *   `IMAGE-CONTENT: document` — the image is an image OF a document;
 *   `IMAGE-CONTENT: scene`    — photograph / rendering of a place.
 * Parsed leniently (case, surrounding whitespace) but only from the
 * FIRST non-empty body line — a mention buried in prose is not a marker.
 */
const IMAGE_CONTENT_MARKER = /^IMAGE-CONTENT:\s*(document|scene)\b/i;

/** Structural document signals for vision bodies that predate (or
 *  ignored) the IMAGE-CONTENT marker. Plural-tolerant, word-bounded. */
const DOC_STRUCTURE_KEYWORDS =
  /\b(documents?|spreadsheets?|screenshots?|scan(?:ned)?|tables?|invoices?|estimates?|forms?|letters?|memos?|worksheets?|ledgers?|line[- ]items?|columns?|rows?|subtotals?|totals?|title\s+block|cost\s+breakdown)\b/gi;

/** Currency tokens — a scene description has no reason to carry these. */
const CURRENCY_TOKENS = /[$€£]|\bUSD\b/g;

/**
 * DIM-1 detector — true when an image upload's `textContent` is a
 * vision extraction of an image OF A DOCUMENT (dense text / table
 * structure), i.e. the upload should flow through the DOCUMENT-style
 * summary → tags → relevance → sedSectionsHelpful path rather than the
 * site-photo stub path.
 *
 * Requires the `[Image vision:` envelope: bare prose under an image
 * MIME (never produced by the pipeline) keeps its existing deferral to
 * the IIA vision pack, and placeholders can never qualify.
 *
 * Decision ladder:
 *   1. explicit `IMAGE-CONTENT: document` / `IMAGE-CONTENT: scene`
 *      marker on the first body line (the model looked at the pixels —
 *      trust it in both directions);
 *   2. structural fallback for marker-less bodies (legacy extractions):
 *      ≥ 2 distinct document-structure keywords AND a numeric signal
 *      (digit density ≥ 5%, ≥ 2 currency tokens, or ≥ 3 lines ending
 *      in a number). The conjunction keeps a site photo whose
 *      description mentions one signboard and a road number from
 *      flipping.
 */
export function looksLikeDocumentImageText(text: string | undefined | null): boolean {
  const body = visionBodyOf(text);
  if (!body || body.length < MIN_USABLE_BODY_CHARS) return false;

  const firstBodyLine = body.split('\n').find(l => l.trim().length > 0)?.trim() ?? '';
  const marker = IMAGE_CONTENT_MARKER.exec(firstBodyLine);
  if (marker) return marker[1]!.toLowerCase() === 'document';

  const keywordHits = new Set(
    (body.match(DOC_STRUCTURE_KEYWORDS) ?? []).map(k => k.toLowerCase().replace(/s$/, '')),
  );
  if (keywordHits.size < 2) return false;

  const nonSpace = body.replace(/\s/g, '');
  const digitDensity = nonSpace.length > 0
    ? (nonSpace.match(/\d/g)?.length ?? 0) / nonSpace.length
    : 0;
  const currencyCount = body.match(CURRENCY_TOKENS)?.length ?? 0;
  const numberEndLines = body
    .split('\n')
    .filter(l => /\d[\d,]*(?:\.\d+)?\s*$/.test(l.trim())).length;

  return digitDensity >= 0.05 || currencyCount >= 2 || numberEndLines >= 3;
}
